import { RefObject } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { calculateScrollOffset, findElementByAttribute } from '../helpers/scroll.helper';
import { TScrollbarElem } from '@shared/components';
import { DOCUMENT_HTML_ATTRIBUTES } from '@modules/documents';

/**
 * `TScrollArgs` is a base interface for both `useScrollContainer` and `useScrollContent` hooks.
 * It defines the arguments needed to scroll to a specific element within a container.
 */
export interface TScrollArgs {
  /**
   * The attribute to query. Defaults to "id" if not provided.
   * If "data-..." we do a querySelector (within the editor’s DOM). Otherwise, getElementById.
   */
  attribute?: string;
  /** The value of that attribute (e.g. "comment-1234"). */
  value: string;
  /** Additional offset in px. E.g. 50 means scroll 50px higher. */
  offset?: number;
  noFocus?: boolean;
  highlightText?: string;
  shouldHighlight?: boolean;
}

/**
 * `Scrollbars` and `TScrollbarElem` are basically the same thing.
 * `TScrollbarElem` is a custom type used for `Scrollbars` component from the `react-custom-scrollbars-2` library.
 */
type TContainerElem = HTMLDivElement | TScrollbarElem;

interface TScrollContainerArgs extends TScrollArgs {
  /** A ref to either an instance of Scrollbars or a normal HTML element */
  containerRef: RefObject<TContainerElem>;
}

/**
 * Unwraps the scrollable DOM element from the given ref.
 */
const getScrollableElement = (containerRef: RefObject<TContainerElem>): HTMLDivElement | null => {
  if (!containerRef.current) return null;

  if (containerRef.current instanceof Scrollbars) {
    // For react-custom-scrollbars-2, the actual scrollable element is available on `.view`
    return (containerRef.current as any).view;
  } else if (containerRef.current instanceof HTMLDivElement) {
    return containerRef.current;
  }
  return null;
};

/**
 * Scrolls the given container to bring the target element into view.
 */
const scrollToElement = (container: HTMLDivElement, element: HTMLElement, offset: number) => {
  const elementRect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const finalScrollTop = calculateScrollOffset(elementRect, containerRect, container.scrollTop, offset);
  container.scrollTo({
    top: finalScrollTop,
    behavior: 'smooth',
  });
};

/**
 * useScrollContainer
 * ------------------
 * Hook that returns a function to scroll a given container (like a list of comments)
 * to a specific child element found by ID or data-attribute.
 */
const useScrollContainer = () => {
  return (args: TScrollContainerArgs) => {
    const { attribute = DOCUMENT_HTML_ATTRIBUTES.DEFAULT_ID, offset = 100, containerRef, value } = args;

    const scrollableElem = getScrollableElement(containerRef);
    if (!scrollableElem) return;

    // Find the child element
    const element: HTMLElement | null = findElementByAttribute(attribute, value, scrollableElem);
    if (!element) return;

    // Scroll to the target element
    scrollToElement(scrollableElem, element, offset);
  };
};

export { useScrollContainer };
